import { Link } from 'react-router'
import { useCopy, useLocale } from '../../content'
import type { ServiceKey } from '../../content/types'
import { SERVICE_MEDIA_ROLE } from '../../data/services'
import { pathFor } from '../../routes'
import { Section } from '../layout/Section'
import { Picture } from '../media/Picture'

/**
 * Three related services at the foot of a service page.
 *
 * The cards share one footprint: the photo sits in a fixed 3:2 box (the
 * service photos run from portrait to 1.58:1, their native ratios would give
 * three different heights), the name reserves two lines and the summary
 * three (`line-clamp-3`), and the grid row stretches every card (`h-full`).
 * The name is the link, stretched over the whole card, so each link reads
 * as the service it leads to; the arrow line is decoration.
 *
 * It follows the (paper) FAQ band, so a hairline marks the seam.
 */
export function RelatedServices({ keys, title }: { keys: readonly ServiceKey[]; title: string }) {
  const copy = useCopy()
  const locale = useLocale()
  return (
    <Section className="border-t border-vc2-hairline">
      <h2 className="vc2-display text-3xl md:text-4xl">{title}</h2>
      <ul className="mt-10 grid gap-6 md:grid-cols-3">
        {keys.map((k) => (
          <li key={k} data-reveal className="h-full">
            <article className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-vc2-hairline bg-vc2-surface transition-shadow hover:shadow-md">
              <div className="relative aspect-[3/2] shrink-0 overflow-hidden">
                <Picture
                  role={SERVICE_MEDIA_ROLE[k]}
                  alt=""
                  sizes="(min-width: 768px) 33vw, 100vw"
                  fill
                  className="absolute inset-0"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="vc2-display line-clamp-2 min-h-[2.5em] text-2xl leading-tight">
                  <Link
                    to={pathFor(k, locale)}
                    className="after:absolute after:inset-0 after:content-[''] hover:underline hover:underline-offset-4"
                  >
                    {copy.pages[k].name}
                  </Link>
                </h3>
                <p className="mt-3 line-clamp-3 min-h-[4.5em] leading-normal text-vc2-ink-soft">{copy.pages[k].short}</p>
                <p aria-hidden="true" className="text-sm font-semibold mt-auto pt-5 text-vc2-ink">
                  {copy.servicesSection.more} →
                </p>
              </div>
            </article>
          </li>
        ))}
      </ul>
    </Section>
  )
}
